// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Every call hits LOCAL SQLite via Tauri IPC (durable, offline, on-disk). This is the
// live-music ledger: many shows over many years (NOT one-per-day). The AI recap reasons
// over a DERIVED-SIGNALS object computed in Rust (see signals.rs / computeSignals).
//
// Data model (mirrors src-tauri/src/db.rs::Show):
//   show { date, artists[], venue, city, cost (cents) + currency, who-with, setlist notes, rating, festival flag }

import { invoke } from "./tauri-web";

export interface Show {
  id: string;
  /** Calendar date you attended (local tz), "YYYY-MM-DD". */
  seen_on: string;
  /** Headliner + support, in order. At least one. */
  artists: string[];
  venue: string;
  city: string;
  /** What you paid, in MINOR units (cents) of `currency`. null = not recorded. */
  cost: number | null;
  /** ISO-4217-ish currency code (uppercased, e.g. "USD", "GBP", "EUR"). */
  currency: string;
  /** Who you went with (free text names). */
  companions: string[];
  /** Free-text setlist / night recollection. Stays on device — never sent to AI. */
  setlist_notes: string;
  /** Your rating of the night, 1..5. 0 = unrated. */
  rating: number;
  is_festival: boolean;
  created_at: string;
  updated_at: string;
  /** Downscaled JPEG data-URL of a photo attached to this show, ON-DEVICE only. "" when unset.
   *  The model never sees it — a photo filed through chat is a memory/attachment, not vision. */
  photo: string;
}

export interface NewShow {
  artists: string[];
  seen_on?: string;
  venue?: string;
  city?: string;
  cost?: number | null;
  currency?: string;
  companions?: string[];
  setlist_notes?: string;
  rating?: number;
  is_festival?: boolean;
}

// cost patching: send cost_set:true to actually apply `cost` (incl. clearing it to null).
export type ShowPatch = Partial<{
  artists: string[];
  seen_on: string;
  venue: string;
  city: string;
  cost: number | null;
  cost_set: boolean;
  currency: string;
  companions: string[];
  setlist_notes: string;
  rating: number;
  is_festival: boolean;
}>;

// ── Derived signals (computed on-device in Rust; only aggregates sent to the brain) ──
// Mirrors src-tauri/src/signals.rs::LedgerSignals. Counts, totals, frequency tables,
// gaps, deltas — never setlist notes / free recollection. This is the payload the AI
// recap reasons over (see brain.ts) so it can name SPECIFIC figures from the ledger.
export interface NameCount { name: string; count: number }
export interface Overall {
  total_shows: number;
  distinct_artists: number;
  distinct_venues: number;
  distinct_cities: number;
  festival_count: number;
  first_show: string | null;
  last_show: string | null;
  years_active: number;
  total_spend_cents: number | null;
  shows_with_cost: number;
  avg_cost_cents: number | null;
  note: string;
}
export interface YearStat {
  year: number;
  shows: number;
  distinct_artists: number;
  distinct_venues: number;
  festival_count: number;
  spend_cents: number | null;
  busiest_month: string | null;
  top_artist: string | null;
}
export interface YearShift {
  recent_year: number;
  prior_year: number;
  shows_delta: number;
  spend_delta_cents: number | null;
  direction: "more" | "fewer" | "same";
}
export interface SpendBreakdown {
  festival_spend_cents: number;
  regular_spend_cents: number;
  festival_shows_with_cost: number;
  regular_shows_with_cost: number;
  bigger_bucket: "festivals" | "regular shows" | "even" | null;
}
export interface MonthStat { month: string; shows: number }
export interface Gap { from_date: string; to_date: string; days: number }
export interface RatedShow { artists: string; date: string; venue: string; rating: number }
export interface Ratings {
  rated_shows: number;
  avg_rating: number | null;
  distribution: [number, number, number, number, number];
  best_rated: RatedShow | null;
}
// A non-obvious cross-signal: a correlation/shift the on-screen bars don't show (e.g. your
// best-rated nights weren't the festivals you spent most on). The recap leads with the
// first one. Strictly observational over the user's own logged shows.
export interface CrossSignal { kind: string; headline: string }
export interface LedgerSignals {
  schema: string;
  generated_for: string;
  currency: string;
  overall: Overall;
  by_year: YearStat[];
  year_shift: YearShift | null;
  top_artists: NameCount[];
  top_venues: NameCount[];
  top_cities: NameCount[];
  top_companions: NameCount[];
  spend_breakdown: SpendBreakdown;
  months: MonthStat[];
  longest_gap: Gap | null;
  ratings: Ratings;
  lead_insights: CrossSignal[];
}

export const store = {
  listShows: (opts?: { from?: string; to?: string; limit?: number }) =>
    invoke<Show[]>("list_shows", { from: opts?.from, to: opts?.to, limit: opts?.limit ?? 50000 }),
  getShow: (id: string) => invoke<Show | null>("get_show", { id }),
  createShow: (input: NewShow) => invoke<Show>("create_show", { input }),
  updateShow: (id: string, patch: ShowPatch) => invoke<void>("update_show", { id, patch }),
  deleteShow: (id: string) => invoke<void>("delete_show", { id }),
  /** Attach a photo (downscaled JPEG data-URL) to a show — filed ON-DEVICE from chat. Never leaves
   *  the device, never sent to the model. "" clears it. */
  setShowPhoto: (id: string, photo: string) => invoke<void>("set_show_photo", { id, photo }),
  /** Compute the derived-signals object on-device. `today` = client local YYYY-MM-DD. */
  computeSignals: (today: string) => invoke<LedgerSignals>("compute_signals", { today }),
};

// ── Pure helpers ──────────────────────────────────────────────────────────────

/** Curated city / common-currency suggestions are tiny; we let the user type freely. */
export const COMMON_CURRENCIES = ["USD", "GBP", "EUR", "CAD", "AUD"] as const;

const toLocalDate = (iso: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};
export const formatLocalDate = (d: Date): string =>
  `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
export const todayLocal = (): string => formatLocalDate(new Date());

/** Money: cents → "$48.00" (best-effort symbol via Intl; falls back to code). */
export function formatMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

/** Parse a free-text money string ("48", "48.50", "$48") into integer cents, or null. */
export function parseMoneyToCents(raw: string): number | null {
  const cleaned = raw.replace(/[^0-9.]/g, "").trim();
  if (!cleaned) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

/** Split a comma-separated free-text field into a clean list (trim, drop empties, dedupe ci). */
export function splitList(raw: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const part of raw.split(",")) {
    const t = part.trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

/** The most-seen artist (and count) across the whole ledger — for the hero. */
export function topArtist(shows: Show[]): { name: string; count: number } | null {
  const counts = new Map<string, number>();
  for (const s of shows) for (const a of s.artists) counts.set(a, (counts.get(a) ?? 0) + 1);
  let best: { name: string; count: number } | null = null;
  for (const [name, count] of counts) {
    if (!best || count > best.count) best = { name, count };
  }
  return best && best.count >= 2 ? best : null;
}

/** Years (descending) present in the ledger — drives the recap year picker. */
export function ledgerYears(shows: Show[]): number[] {
  const ys = new Set<number>();
  for (const s of shows) {
    const d = toLocalDate(s.seen_on);
    if (d) ys.add(d.getFullYear());
  }
  return [...ys].sort((a, b) => b - a);
}

export const STAR = "★";
export function stars(rating: number): string {
  if (rating < 1 || rating > 5) return "";
  return STAR.repeat(rating) + "☆".repeat(5 - rating);
}
